'use client'

import { useEffect } from 'react'
import { useRouter } from 'next/navigation'

type KeyboardNavProps = {
  prevHref?: string
  nextHref?: string
}

/**
 * Left/right arrow keys walk the auction run, the same way the two chevrons in
 * the header do.
 *
 * It renders nothing — it is a behaviour, not a control — so the hero's layout
 * is identical with or without it, and a browser with JavaScript off simply
 * falls back to clicking the chevrons.
 */
// The return type is spelled out because the project runs with
// `strictNullChecks: false`, where a bare `return null` infers as `any`.
const KeyboardNav = ({ prevHref, nextHref }: KeyboardNavProps): null => {
  const router = useRouter()

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // Leave browser and OS shortcuts (⌥←, ⌘←…) alone.
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
        return
      }

      // Never steal the arrow keys from something the visitor is typing in.
      const target = event.target as HTMLElement | null
      if (
        target?.isContentEditable ||
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(target?.tagName ?? '')
      ) {
        return
      }

      const href =
        // eslint-disable-next-line no-nested-ternary
        event.key === 'ArrowLeft'
          ? prevHref
          : event.key === 'ArrowRight'
            ? nextHref
            : undefined

      if (!href) return

      event.preventDefault()
      router.push(href)
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [prevHref, nextHref, router])

  return null
}

export default KeyboardNav
